import axios, { AxiosError } from 'axios';
import type { AxiosInstance, InternalAxiosRequestConfig } from 'axios';

import { useAuthStore } from '@/store/auth';
import { API_CONFIG } from '@/config/api.config';
import { env } from '@/config/env';

export const BASE_URL = API_CONFIG.BASE_URL;
const DEFAULT_TOKEN = 'eyJhbGciOiJIUzI1NiJ9.eyJyb2xlIjoiQURNSU4iLCJzdWIiOiJhZG1pbiIsImlhdCI6MTc3NDUwODU1NywiZXhwIjoxNzc0NTk0OTU3fQ.2zJAAnFXlc1l1mZAsNAaEaHnTWERIRdydi-jARiyIbQ';

/**
 * Global Axiost instance with interceptors for 
 * authentication and error handling.
 */
const apiClient: AxiosInstance = axios.create({
  baseURL: BASE_URL,
  headers: {
    'accept': '*/*',
    'Content-Type': 'application/json',
  },
});

// Request Interceptor: Inject Authentication Token
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // Dynamically retrieve the token from the persistent store
    const token = useAuthStore.getState().token;
    config.headers.Authentication = `Bearer ${token || DEFAULT_TOKEN}`;

    // Log outgoing requests during development
    if (env.DEV) {
      console.log(`[API Request] ${config.method?.toUpperCase()} ${config.url}`, config.data || '');
    }

    return config;
  },
  (error: AxiosError) => {
    return Promise.reject(error);
  }
);

// Response Interceptor: Global Error Handling
apiClient.interceptors.response.use(
  (response) => {
    if (env.DEV) {
      console.log(`[API Response] ${response.status} ${response.config.url}`, response.data);
    }
    return response;
  },
  (error: AxiosError) => {
    const status = error.response?.status ?? null;

    if (status === 401 || status === 403) {
      console.error(`[API Error] ${status === 401 ? 'Unauthorized' : 'Forbidden'} - ${status}. Logging out.`);
      useAuthStore.getState().logout();
      window.location.href = '/login';
    } else if (status === 404) {
      console.error('[API Error] Resource Not Found - 404.');
    } else if (status === 500) {
      console.error('[API Error] Internal Server Error - 500.');
    } else {
      console.error(`[API Error] ${error.message}`);
    }

    return Promise.reject(error);
  }
);

export default apiClient;
